import { useRef, useState } from 'react';

export interface UploadProgress {
  /** Die gerade laufende Datei, ab 1 gezählt. */
  current: number;
  total: number;
}

export interface UploadOutcome {
  uploaded: number;
  failed: number;
  /** Über die Grenze hinaus gewählt und deshalb gar nicht erst geschickt. */
  skipped: number;
}

/**
 * Mehrere Dateien hochladen — nacheinander, weil jedes Foto zum Verkleinern im Browser voll
 * dekodiert wird und viele zugleich am Handy den Speicher sprengen. Scheitert eine Datei, laufen
 * die übrigen weiter; ihren Grund meldet `uploadOne` selbst. Was über `room` hinausgeht, wird nicht
 * geschickt, statt am Server einzeln abzuprallen.
 */
export function useUploadQueue() {
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const running = useRef(false);

  async function run(
    files: FileList | File[],
    room: number,
    uploadOne: (file: File) => Promise<unknown>,
  ): Promise<UploadOutcome> {
    const all = Array.from(files);
    const batch = all.slice(0, Math.max(0, room));
    if (running.current || batch.length === 0) {
      return { uploaded: 0, failed: 0, skipped: all.length - batch.length };
    }
    running.current = true;
    let uploaded = 0;
    try {
      for (const [index, file] of batch.entries()) {
        setProgress({ current: index + 1, total: batch.length });
        const ok = await uploadOne(file).then(
          () => true,
          () => false,
        );
        if (ok) uploaded += 1;
      }
    } finally {
      running.current = false;
      setProgress(null);
    }
    return { uploaded, failed: batch.length - uploaded, skipped: all.length - batch.length };
  }

  return { run, progress, busy: progress !== null };
}
